import { useIntl } from 'react-intl';
import { useTheme, useMediaQuery } from '@material-ui/core';
import PropTypes from 'prop-types';

import CustomButton from '../buttons/custom-button/CustomButton';
import NotificationTutorialImg from '../../icons/notification-tutorial-img/NotificationTutorialImg';

import {
  isNotificationSupported,
  getNotificationPermission,
} from 'utils/NotificationUtils';
import useStyles from './CustomAllowNotifications.styles';
import NotificationPermissionsType from '../../../constants/NotificationPermissionsType';

const content = {
  initial: {
    textAllow: 'globals.notifications.allow',
    textNotif: 'globals.notifications.notifications',
    allowText: 'globals.notifications.allow.text',
    acceptLabel: 'globals.notifications.allow',
    cancelLabel: 'globals.notifications.cancel',
  },
  needsConfirmation: {
    textAllow: 'globals.notifications.allow.orders',
    textNotif: 'globals.notifications.allow.orders.notifications',
    allowText: 'globals.notifications.allow.orders.text',
    acceptLabel: 'globals.notifications.allow',
    cancelLabel: 'globals.notifications.orders.cancel',
  },
  denied: {
    textAllow: 'globals.notifications.denied.orders',
    textNotif: 'globals.notifications.allow.warning.title',
    allowText: 'globals.notifications.allow.warning',
    warningItem1: 'globals.notifications.allow.warningItem1',
    warningItem2: 'globals.notifications.allow.warningItem2',
    acceptLabel: 'globals.notifications.denied.understood',
  },
};

export function CustomAllowNotifications(props) {
  const { state, onAcknowledge, onAllow, onCancel } = props;
  const { formatMessage } = useIntl();

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const validState = content[state] ? state : 'initial';
  const {
    textAllow,
    textNotif,
    allowText,
    warningItem1,
    warningItem2,
    acceptLabel,
    cancelLabel,
  } = content[validState];

  const textNotifWarning = isMdUp
    ? formatMessage({
        id: 'globals.notifications.allow.warning.title',
      })
    : formatMessage({ id: 'globals.notifications.notifications' });

  const classes = useStyles({
    isPermissionUndefinedOrDenied:
      !isNotificationSupported() ||
      getNotificationPermission() === NotificationPermissionsType.DENIED,
  });

  return (
    <div className={classes.box}>
      <div className={classes.root}>
        <NotificationTutorialImg className={classes.tutorialImg} />
        <div>
          <header className={classes.header}>
            <p>{formatMessage({ id: textAllow })}</p>
            <p>
              {state === 'denied'
                ? textNotifWarning
                : formatMessage({ id: textNotif })}
            </p>
          </header>
          <div className={classes.text}>
            {formatMessage({ id: allowText })}
            {warningItem1 && warningItem2 && (
              <ol className={classes.warningList}>
                <li
                  className={classes.warningListItem}
                  dangerouslySetInnerHTML={{
                    __html: formatMessage({ id: warningItem1 }),
                  }}
                />
                <li className={classes.warningListItem}>
                  {formatMessage({ id: warningItem2 })}
                </li>
              </ol>
            )}
          </div>
        </div>
      </div>
      <div className={classes.cta}>
        {!!onAllow && (
          <CustomButton
            text={formatMessage({ id: acceptLabel })}
            variant='contained'
            color='primary'
            onClick={onAllow}
            classes={{ submitButton: classes.primary }}
          />
        )}
        {!!onAcknowledge && !isMdUp && (
          <CustomButton
            text={formatMessage({ id: acceptLabel })}
            variant='contained'
            color='secondary'
            onClick={onAcknowledge}
          />
        )}
        {!!onCancel && (
          <CustomButton
            text={formatMessage({ id: cancelLabel })}
            classes={{ submitButton: classes.cancelBtn }}
            onClick={onCancel}
          />
        )}
      </div>
    </div>
  );
}

CustomAllowNotifications.propTypes = {
  state: PropTypes.oneOf(['initial', 'needsConfirmation', 'denied']),
  onAcknowledge: PropTypes.func,
  onAllow: PropTypes.func,
  onCancel: PropTypes.func,
};
